/** What a run leaves for the operator, one card per item: a decision or an email.
 * Each carries a Reply control that quotes it, with the id the
 * agent's settling tools take, into the composer - several quotes build one reply.
 * See docs/Y2026-Q3/design-specialist-first-run-output.md 3.1. */
import { type ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";

import type { RunEmail } from "../api/hooks/useStatefulAgents";
import { formatWhen } from "../utils/formatWhen";
import { EmailDraft } from "./EmailDraft";
import styles from "./RunItemCards.module.css";
import { Chevron } from "./Chevron";

/** Appends a quote to the run's composer, or null where there is no composer. ``from``
 * is the chip pressed, so a composer that opens beside it can. */
export type OnReply = ((quote: string, from?: Element) => void) | null;

// Names where it came from: the card may be from before a compaction, when the
// agent no longer has it in view.
const replyQuote = (
  kind: string,
  heading: string,
  id: string,
  where: string[] = [],
): string =>
  `> Re: ${[...where.filter(Boolean), `[${kind}] ${heading} (${id})`].join(": ")}\n\n`;

const ReplyChip = ({
  onReply,
  quote,
  label = "Respond to agent",
}: {
  onReply: OnReply;
  quote: string;
  label?: string;
}) =>
  onReply ? (
    <button
      type="button"
      className={styles.chip}
      onClick={(e) => onReply(quote, e.currentTarget)}
    >
      {label}
    </button>
  ) : null;

const Card = ({
  id,
  kind,
  title,
  meta,
  actions,
  settled,
  defaultOpen,
  children,
}: {
  /** The item's id, so a link elsewhere can land on its card. */
  id: string;
  kind: string;
  title: ReactNode;
  meta: ReactNode;
  actions: ReactNode;
  settled: boolean;
  defaultOpen: boolean;
  children: ReactNode;
}) => {
  const landed = useLocation().hash === `#${id}`;
  // A card a link landed on opens, so what it was sent to is readable.
  const [open, setOpen] = useState(defaultOpen || landed);
  const className = [
    styles.card,
    settled && styles.settled,
    landed && styles.landed,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div id={id} className={className}>
      <div className={styles.head}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          aria-label={open ? "Collapse" : "Expand"}
          onClick={() => setOpen(!open)}
        >
          <Chevron tree open={open} />
        </button>
        <span className={styles.kind}>{kind}</span>
        <button
          type="button"
          className={styles.title}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {title}
        </button>
        <span className={styles.actions}>{actions}</span>
      </div>
      <div className={styles.meta}>{meta}</div>
      {/* Hidden rather than unmounted, so an email's unsent edits survive a fold. */}
      <div className={styles.body} hidden={!open}>
        {children}
      </div>
    </div>
  );
};

const emailHeading = (email: RunEmail): string =>
  `${email.to || "no recipient"} - ${email.subject || "(no subject)"}`;

/** `inline` when the card sits in its author's answer, so the author goes without saying.
 * `defaultOpen` shows the email itself, as the agent pane does. */
export const EmailCard = ({
  email,
  onReply,
  inline,
  projectName,
  defaultOpen,
}: {
  email: RunEmail;
  onReply: OnReply;
  inline: boolean;
  projectName: string;
  defaultOpen: boolean;
}) => {
  const sent = email.status === "sent";
  // Replaced by a later email to the same person, from this run or another.
  const replaced = email.status === "superseded";
  const later = !sent && !replaced && email.shown_later;
  // "Send now" is the default, so only a condition or a date is worth showing.
  const trigger =
    email.trigger.trim().toLowerCase() === "now" ? "" : email.trigger;
  // An email waiting since an earlier day says so: its wording may have aged.
  const drafted =
    email.first_drafted_at &&
    new Date(email.first_drafted_at).toDateString() !==
      new Date().toDateString()
      ? `drafted ${new Date(email.first_drafted_at).toLocaleDateString(
          "en-GB",
          {
            day: "numeric",
            month: "short",
          },
        )}`
      : "";
  const facts = [
    inline ? "" : `by ${email.author_label || "the agent"}`,
    !sent ? drafted : "",
    email.merges.length > 0 ? `merged from ${email.merges.length}` : "",
    // Sent-detection only sees mail on the project's ingested threads.
    sent ? `sent ${email.sent_at ? formatWhen(email.sent_at) : ""}`.trim() : "",
    replaced ? "out of date - replaced by a later email, do not send" : "",
    later ? "out of date here - still waiting, on the latest analysis" : "",
    !sent && !replaced && trigger ? `send ${trigger}` : "",
  ].filter(Boolean);
  return (
    <Card
      id={email.draft_id}
      defaultOpen={defaultOpen}
      kind="Email"
      title={
        <span className={styles.emailTitle}>
          <span>Subject: {email.subject || "(no subject)"}</span>
          <span>To: {email.to || "no recipient"}</span>
        </span>
      }
      settled={sent || replaced || later}
      meta={
        <>
          {email.why || "No reason given."}
          {facts.length > 0 && (
            <div className={styles.facts}>{facts.join(" · ")}</div>
          )}
        </>
      }
      actions={
        !sent &&
        !replaced &&
        !later && (
          <ReplyChip
            onReply={onReply}
            quote={replyQuote("Email", emailHeading(email), email.draft_id, [
              projectName,
              email.author_label,
            ])}
          />
        )
      }
    >
      {email.discharges.length > 0 && (
        <>
          <h5 className={styles.label}>Completes these open actions</h5>
          <ul className={styles.list}>
            {email.discharges.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </>
      )}
      {email.depends_on.length > 0 && (
        <p className={styles.waits}>Waits on: {email.depends_on.join(", ")}</p>
      )}
      <EmailDraft
        draft={{
          to: email.to,
          subject: email.subject,
          body: email.body,
          thread: email.thread,
        }}
        draftId={email.draft_id}
      />
    </Card>
  );
};
